import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/useAuth";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card } from "@/components/ui/Card";
import { Mail, ArrowRight } from "lucide-react";

export function LoginPage() {
  const { sendOtp, login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (isAuthenticated) {
    navigate("/writer/dashboard", { replace: true });
    return null;
  }

  const handleSendOtp = async () => {
    setLoading(true);
    setError("");
    try {
      await sendOtp(email);
      setStep("code");
    } catch {
      setError("Failed to send verification code. Check your email.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    setLoading(true);
    setError("");
    try {
      await login(email, code);
      navigate("/writer/dashboard", { replace: true });
    } catch {
      setError("Invalid code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-sm mx-auto px-4 py-20">
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <div className="p-3 rounded-full bg-orange/10">
            <Mail className="text-orange" size={24} />
          </div>
        </div>
        <h1 className="text-2xl font-display font-bold mb-2">
          Writer Login
        </h1>
        <p className="text-grey font-body text-sm">
          Sign in to publish and manage your articles.
        </p>
      </div>

      <Card className="p-6">
        {step === "email" ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSendOtp();
            }}
          >
            <Input
              label="Email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={error}
            />
            <Button
              type="submit"
              className="w-full mt-4"
              loading={loading}
              disabled={!email}
            >
              Continue
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </form>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleVerify();
            }}
          >
            <p className="text-sm text-grey font-body mb-4">
              We sent a 6-digit code to{" "}
              <strong className="text-black">{email}</strong>
            </p>
            <Input
              label="Verification Code"
              type="text"
              placeholder="000000"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              error={error}
              autoFocus
            />
            <Button
              type="submit"
              className="w-full mt-4"
              loading={loading}
              disabled={code.length !== 6}
            >
              Verify
              <ArrowRight size={16} className="ml-2" />
            </Button>
            <button
              type="button"
              className="w-full text-center text-sm text-grey font-body mt-3 hover:text-black"
              onClick={() => {
                setStep("email");
                setCode("");
                setError("");
              }}
            >
              Use a different email
            </button>
          </form>
        )}
      </Card>

      <a
        href="https://testflight.apple.com/join/ZYTDH2bd"
        target="_blank"
        rel="noopener noreferrer"
        className="block text-center mt-6 text-sm font-body text-grey underline hover:text-orange transition-colors"
      >
        Don't have a Liminal account? Sign up here
      </a>
    </div>
  );
}
